import { configuration, createWLipSyncNode, parseBinaryProfile } from 'wlipsync/wlipsync.js'
import localAssetStore from './localAssetStore'

function isBinaryProfile(record) {
  if (!record) return false
  const name = String(record.name || '').toLowerCase()
  if (name.endsWith('.bin')) return true
  const type = String(record.type || '')
  return type === 'application/octet-stream'
}

async function readRecordAsText(record) {
  if (!record) return null
  if (record.dataUrl) {
    const base64 = record.dataUrl.split(',')[1] || ''
    const decoded = atob(base64)
    return decoded
  }
  if (record.blob) return await record.blob.text()
  return null
}

async function readRecordAsArrayBuffer(record) {
  if (!record) return null
  if (record.blob) return await record.blob.arrayBuffer()
  if (record.dataUrl) {
    const base64 = record.dataUrl.split(',')[1] || ''
    const binary = atob(base64)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
    return bytes.buffer
  }
  return null
}

class LipSyncEngine {
  constructor() {
    this._wasmModulePromise = null
    this._workletContexts = new WeakSet()
  }

  _assetUrl(path) {
    const base = (import.meta.env.BASE_URL || '/').replace(/\/?$/, '/')
    return `${base}assets/wlipsync/${path}`
  }

  async _loadWasmModule() {
    if (configuration.wasmModule) return configuration.wasmModule
    if (this._wasmModulePromise) return this._wasmModulePromise

    this._wasmModulePromise = (async () => {
      const wasmUrl = this._assetUrl('wlipsync.wasm')
      if (typeof WebAssembly.compileStreaming === 'function') {
        try {
          const module = await WebAssembly.compileStreaming(fetch(wasmUrl))
          configuration.wasmModule = module
          return module
        } catch {
          // Fallback to arrayBuffer compile for incompatible MIME/proxies.
        }
      }

      const response = await fetch(wasmUrl)
      if (!response.ok) {
        throw new Error(`Failed to fetch lip sync wasm (${response.status})`)
      }
      const buffer = await response.arrayBuffer()
      const module = await WebAssembly.compile(buffer)
      configuration.wasmModule = module
      return module
    })()

    try {
      return await this._wasmModulePromise
    } finally {
      if (!configuration.wasmModule) {
        this._wasmModulePromise = null
      }
    }
  }

  async _ensureAudioWorklet(audioContext) {
    if (!audioContext?.audioWorklet) {
      throw new Error('AudioWorklet is not available in this browser/context')
    }
    if (this._workletContexts.has(audioContext)) return
    const workletUrl = this._assetUrl('audio-processor.js')
    await audioContext.audioWorklet.addModule(workletUrl)
    this._workletContexts.add(audioContext)
  }

  async ensureRuntime(audioContext) {
    await this._loadWasmModule()
    await this._ensureAudioWorklet(audioContext)
  }

  async loadProfileFromRecord(record) {
    if (!record) return null
    if (isBinaryProfile(record)) {
      const buffer = await readRecordAsArrayBuffer(record)
      if (!buffer) return null
      return parseBinaryProfile(buffer)
    }

    const text = await readRecordAsText(record)
    if (!text) return null
    try {
      return JSON.parse(text)
    } catch {
      return null
    }
  }

  async loadProfileById(id) {
    if (!id) return null
    const record = await localAssetStore.getAsset(id)
    return this.loadProfileFromRecord(record)
  }

  async createNode(audioContext, profile) {
    if (!audioContext || !profile) return null
    await this.ensureRuntime(audioContext)
    return await createWLipSyncNode(audioContext, profile)
  }
}

export default new LipSyncEngine()
